// src/contexts/sectorDashboardV2/SectorTokenAnalyticsProvider.tsx
import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { DashboardType } from "@/types/dashboards/dashboardTypes";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import { fetchHistoryBatched } from "@/services/queries/charts/chartQueries";
import { MetricKeys } from "@/constants/metrics";
import { tokenHistoryQueryOptions } from "@/services/queries/dashboards/tokenHistoryQueries";
import { toReturns } from "@/lib/metrics";
import {
  SectorTokenAnalyticsContext,
  type SectorTokenAnalyticsContextValue,
} from "@/contexts/sectorDashboardV2/sectorTokenAnalyticsContext";

export const SectorTokenAnalyticsProvider: React.FC<{
  dashboardType: DashboardType;
  children: React.ReactNode;
}> = ({ dashboardType, children }) => {
  const { maps } = useCategoriesTree();
  const categoryId = useMemo(() => {
    const codeToId = maps.codeToId as unknown as Record<string, number> | undefined;
    return codeToId?.[String(dashboardType)] ?? -1;
  }, [dashboardType, maps.codeToId]);

  const sectorPriceHistoryQuery = useQuery({
    queryKey: ["history", "metrics", { categoryId, keys: [MetricKeys.PriceClose] }],
    enabled: categoryId > 0,
    queryFn: ({ signal }) => fetchHistoryBatched("metrics", { categoryId, keys: [MetricKeys.PriceClose], signal }),
    staleTime: 10 * 60 * 1000,
  });

  const btcPriceQuery = useQuery({
    ...tokenHistoryQueryOptions("BTC", [MetricKeys.PriceClose]),
    staleTime: 10 * 60 * 1000,
  });

  const ethPriceQuery = useQuery({
    ...tokenHistoryQueryOptions("ETH", [MetricKeys.PriceClose]),
    staleTime: 10 * 60 * 1000,
  });

  const pricesByTicker = useMemo(() => {
    const map = new Map<string, Array<number | null>>();
    const metric = sectorPriceHistoryQuery.data?.metrics?.find((m) => m.metric_key === MetricKeys.PriceClose);
    for (const series of metric?.tokens ?? []) {
      const vals = (series.values ?? []).map((v) => (v == null ? null : Number(v)));
      map.set(series.ticker, vals);
    }
    return map;
  }, [sectorPriceHistoryQuery.data]);

  const priceDates = useMemo(() => {
    const metric = sectorPriceHistoryQuery.data?.metrics?.find(
      (m) => m.metric_key === MetricKeys.PriceClose
    );
    return (metric?.dates ?? []) as number[];
  }, [sectorPriceHistoryQuery.data]);

  const returnsByTicker = useMemo(() => {
    const map = new Map<string, number[]>();
    for (const [ticker, prices] of pricesByTicker.entries()) {
      const r = toReturns(prices.map((v) => (v == null ? null : v)));
      map.set(ticker, r.map((x) => Number(x ?? 0)));
    }
    return map;
  }, [pricesByTicker]);

  const btcReturns = useMemo(() => {
    const metric = btcPriceQuery.data?.metrics?.find((m) => m.metric_key === MetricKeys.PriceClose);
    const prices = metric?.values ?? [];
    if (!prices || prices.length < 10) return null;
    const r = toReturns(prices.map((v) => (v == null ? null : Number(v))));
    return r.map((x) => Number(x ?? 0));
  }, [btcPriceQuery.data]);

  const ethReturns = useMemo(() => {
    const metric = ethPriceQuery.data?.metrics?.find((m) => m.metric_key === MetricKeys.PriceClose);
    const prices = metric?.values ?? [];
    if (!prices || prices.length < 10) return null;
    const r = toReturns(prices.map((v) => (v == null ? null : Number(v))));
    return r.map((x) => Number(x ?? 0));
  }, [ethPriceQuery.data]);

  const isLoading =
    sectorPriceHistoryQuery.isLoading ||
    sectorPriceHistoryQuery.isFetching ||
    btcPriceQuery.isLoading ||
    ethPriceQuery.isLoading;

  const isError = Boolean(sectorPriceHistoryQuery.isError || btcPriceQuery.isError || ethPriceQuery.isError);
  const error =
    (sectorPriceHistoryQuery.error as Error | null) ??
    (btcPriceQuery.error as Error | null) ??
    (ethPriceQuery.error as Error | null);

  const value: SectorTokenAnalyticsContextValue = {
    categoryId,
    priceDates,
    pricesByTicker,
    returnsByTicker,
    btcReturns,
    ethReturns,
    isLoading,
    isError,
    error,
  };

  return <SectorTokenAnalyticsContext.Provider value={value}>{children}</SectorTokenAnalyticsContext.Provider>;
};


